import React, { useState } from 'react'
import styled, { css } from 'styled-components'

import Box from '@components/Box'

import { above } from '@styles/mixins/breakpoints'
import { STypographyDesc } from '@components/Typography'
import { IFoldCalculated, IValues } from './types'
import { defaultValues, folders } from './data'
import Control from './Control'
import Result from './Result'
import Fold from './Fold'

import { getCalculatedData } from './calculations'

export const SCalc = styled.div`
  margin-bottom: 60px;

  ${above('md')(css`
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
    box-shadow: ${({ theme }) => theme.general.boxShadow};
    border-radius: 8px;
    padding: 32px;
    margin-bottom: 32px;
  `)}

  ${above('xl')(css`
    display: flex;
    padding: 20px 52px;
  `)}
`

export const SDevider = styled.div`
  margin: 56px 0 32px 0;
  flex-grow: 0;
  flex-shrink: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.steelGray};

  ${above('xl')(css`
    margin: 0 52px;
    width: 1px;
    min-height: 100%;
    border-top: none;
    border-right: 1px solid ${({ theme }) => theme.colors.steelGray};
  `)}
`

const SFold = styled(Fold)`
  &:not(:last-child) {
    margin-bottom: 12px;
  }
`

const Calc: React.FC = () => {
  const [values, setValues] = useState<IValues>(defaultValues)
  const {
    minimumContributionPercent,
    contributionSize,
    contributionSizePercent,
    employerContributionSize,
    employerContributionSizePercent,
    savingsOnIPS,
    ipsContributionsPaid,
    ipsAccruedInvestmentIncome,
    ipsFirstPayment,
    ipsPensionAmount,
    ipsPensionAmountAlt,
    savingsOnKIPS,
    kipsContributionsPaid,
    kipsAccruedInvestmentIncome,
    kipsFirstPayment,
    kipsPensionAmount,
    kipsPensionAmountAlt,
    numberOfSalaries,
    estimatedYearsOfWork,
    kepvOnetimeContribution,
    kepvPensionAmount,
    kepvFirstPayment,
    kepvPensionAmountAlt,
    warning,
  } = getCalculatedData(values)

  const foldCalculated: IFoldCalculated[] = [
    {
      value: savingsOnIPS,
      accruedInvestmentIncome: ipsAccruedInvestmentIncome,
      contributionsPaid: ipsContributionsPaid,
      firstOptionPayment: ipsFirstPayment,
      firstOptionPension: ipsPensionAmount,
      secondOptionPension: ipsPensionAmountAlt,
    },
    {
      value: savingsOnKIPS,
      accruedInvestmentIncome: kipsAccruedInvestmentIncome,
      contributionsPaid: kipsContributionsPaid,
      firstOptionPayment: kipsFirstPayment,
      firstOptionPension: kipsPensionAmount,
      secondOptionPension: kipsPensionAmountAlt,
    },
    {
      value: kepvOnetimeContribution,
      numberOfSalaries: numberOfSalaries,
      firstOptionPayment: kepvFirstPayment,
      firstOptionPension: kepvPensionAmount,
      secondOptionPension: kepvPensionAmountAlt,
    },
  ]

  return (
    <Box>
      <SCalc>
        <Box width={{ _: '100%', xl: '375px' }} py={{ _: '0', xl: '20px' }} flexGrow={0} flexShrink={0}>
          <Control
            minimumContribution={minimumContributionPercent}
            estimatedYearsOfWork={estimatedYearsOfWork}
            values={values}
            onChange={setValues}
          />
        </Box>
        <SDevider />
        <Box width="100%" py={{ _: '0', xl: '20px' }}>
          <Result
            {...{
              warning,
              contributionSize,
              contributionSizePercent,
              employerContributionSize,
              employerContributionSizePercent,
            }}
          />
        </Box>
      </SCalc>
      <STypographyDesc mb="24px" textAlign="center">
        Не является офертой и не гарантирует доходность в будущем. Расчет проиллюстрирован при ожидаемой доходности в
        размере 4% годовых.
      </STypographyDesc>
      {folders.map((folder, i) => {
        return <SFold key={folder.heading} {...folder} {...foldCalculated[i]} />
      })}
    </Box>
  )
}

export default Calc
